import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaSettings, User } from "../AuthContext";
import ObservatorHandler from "./ObservatorHandler";
import { UserIcon, Video, X } from "lucide-react";
import Scene from "./Scene";
import { RoomAccessRequest, SessionSettingsResponse, AccessRoomResponseOptions } from "./types";
import ActionButtons from "./ActionButtons";
import type DyteClient from "@dytesdk/web-core";
import { useDyteClient } from "@dytesdk/react-web-core";
import Swal from "sweetalert2";
import { getFunctions, httpsCallable } from "firebase/functions";
import { addUserToRoom, removeUserFromRoom, db, createVirtualAvatar, getRoleInSession } from "../../firebase";
import { collection, doc, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import InviteUserModal from "./InviteUserModal";
import DyteMeet from "./DyteMeet";
import ParticipantsPanelModal from "./ParticipantsPanelModal";
import VirtualAvatarModal from "./VirtualAvatarModal";
import { useCreditBalance } from "../../hooks/useCreditBalance";

type RoomProps = {
    user: User | null;
    roomId: string;
    sessionSetting: SessionSettingsResponse | null;
    selectedAvatarId: string | null;
}

const Room: React.FC<RoomProps> = ({ user, roomId, sessionSetting, selectedAvatarId }) => {
    const functions = getFunctions();
    const navigate = useNavigate();

    const destroySessionCallable = httpsCallable(
        functions,
        "destroySession",
    );

    const cancelRoomAccessRequestFunction = httpsCallable<{ roomId: string }, { success: boolean; message: string }>(
        functions,
        "cancelRoomAccessRequest",
    );

    const [isMobile, setIsMobile] = useState(false);
    const [activeTab, setActiveTab] = useState<"scene" | "video">("video");
    const [isPortrait, setIsPortrait] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [isLeavingRoom, setIsLeavingRoom] = useState(false);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isVirtualAvatarModalOpen, setIsVirtualAvatarModalOpen] = useState(false);
    const [accessRequests, setAccessRequests] = useState<RoomAccessRequest[]>([]);
    const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);
    const previousPendingRequestsCount = useRef(0);
    const hasInitializedRequests = useRef(false);
    const hasShownLowCreditAlert = useRef(false);

    const { hoursBalance, isLowBalance } = useCreditBalance(
        user?.uid,
        sessionSetting?.isRoomModerator || false
    );

    const [meetingClient, setMeetingClient] = useState<DyteClient | null>(null);
    const [, initMeeting] = useDyteClient();
    const initMeetingRef = useRef(initMeeting);
    const isInitializingRef = useRef(false);
    const { mediaSettings: nestedMediaSettings } = useMediaSettings();
    const stableMediaSettings = useMemo(
        () => nestedMediaSettings,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(nestedMediaSettings)],
    );

    const handleSceneError = useCallback((error: string) => {
        Swal.fire({
            icon: 'error',
            title: 'Erro na sala',
            text: error,
        });
    }, []);

    const checkOrientation = useCallback(() => {
        const mobile = window.innerWidth < 1024;
        setIsPortrait(mobile && window.innerHeight > window.innerWidth);
        setIsMobile(mobile);
        if (mobile && window.innerHeight > window.innerWidth) setActiveTab("video");
    }, []);

    useEffect(() => {
        checkOrientation();
        window.addEventListener("resize", checkOrientation);
        window.addEventListener("orientationchange", () =>
            setTimeout(checkOrientation, 100),
        );
        return () => {
            window.removeEventListener("resize", checkOrientation);
            window.removeEventListener("orientationchange", () =>
                setTimeout(checkOrientation, 100),
            );
        };
    }, [checkOrientation]);

    const initializeAndJoinDyte = useCallback(async () => {
        if (isInitializingRef.current || meetingClient) {
            return;
        }

        isInitializingRef.current = true;
        let meeting: DyteClient | undefined;

        try {
            setLoadingMessage("Obtendo token de autenticação...");
            const getDyteMeeting = httpsCallable<
                { room: string; roleInSession?: string },
                { authToken: string; meetingId: string }
            >(functions, "getDyteMeetingV2");

            // PASSO 1: Obter token do Dyte (cria/reusa meeting)
            const result = await getDyteMeeting({
                room: roomId,
                roleInSession: getRoleInSession(sessionSetting?.sessionRole || "participant")
            });

            const authToken = result.data.authToken;

            setLoadingMessage("Inicializando reunião...");

            // PASSO 2: Inicializar cliente Dyte
            meeting = await initMeetingRef.current({
                authToken,
                defaults: {
                    audio: stableMediaSettings.audio,
                    video: stableMediaSettings.video,
                },
            });

            if (!meeting) {
                console.error("❌ Meeting client não foi criado");
                return;
            }

            setLoadingMessage("Entrando na sala...");
            await meeting.joinRoom();

            await addUserToRoom(
                roomId,
                user as User,
                sessionSetting?.sessionRole || "participant",
                selectedAvatarId
            );

            setMeetingClient(meeting);
        } catch (error) {
            console.error(error);
            setLoadingMessage("Erro ao entrar na sala.");

            Swal.fire({
                icon: "error",
                title: "Erro ao entrar na sala",
                text: "Ocorreu um erro ao tentar entrar na sala. Por favor, tente novamente mais tarde.",
            });
        } finally {
            isInitializingRef.current = false;
        }
    }, [functions, meetingClient, roomId, selectedAvatarId, sessionSetting?.sessionRole, stableMediaSettings, user]);

    useEffect(() => {
        initializeAndJoinDyte();
    }, [initializeAndJoinDyte]);

    const disableStreams = useCallback(() => {
        if (meetingClient?.self) {
            meetingClient.self.disableVideo();
            meetingClient.self.disableAudio();
            meetingClient.self.disableScreenShare();
        }
    }, [meetingClient]);

    // Effect separado para cleanup do meetingClient
    useEffect(() => {
        const dyteMeetingsRef = doc(db, "dyteMeetings", roomId);
        const unsubscribeDyteMeetingListener = onSnapshot(
            dyteMeetingsRef,
            (docSnapshot) => {
                // Se o documento não existe, significa que a sessão foi encerrada
                if (!docSnapshot.exists()) {
                    // Desconectar do Dyte
                    if (!sessionSetting?.isRoomModerator) {
                        Swal.fire({
                            icon: "info",
                            title: "Sessão encerrada",
                            text: "Esta sessão foi encerrada pelo terapeuta. Você será redirecionado.",
                        }).then(() => {
                            navigate("/");
                        });
                    }
                }
            },
            (error) => {
                console.error("❌ ERRO ao monitorar documento dyteMeetings:", error);
            },
        );

        return () => {
            unsubscribeDyteMeetingListener();
        };
    }, []);

    const handleModeratorLeave = async () => {
        await destroySessionCallable(roomId);
    };

    const handleCreateVirtualAvatar = useCallback(
        async ({ name, avatarId }: { name: string; avatarId: string }) => {
            if (!roomId || !user) {
                return;
            }

            if (!sessionSetting?.isRoomModerator) {
                Swal.fire({
                    icon: "error",
                    title: "Ação não permitida",
                    text: "Somente o moderador pode adicionar avatares ao cenário.",
                });
                return;
            }

            try {
                await createVirtualAvatar(roomId, {
                    name,
                    avatarId,
                    createdBy: user.uid,
                    createdByName: user.name || user.email || null,
                });

                setIsVirtualAvatarModalOpen(false);
            } catch (error) {
                console.error("Erro ao criar avatar virtual:", error);
                Swal.fire({
                    icon: "error",
                    title: "Erro ao adicionar avatar",
                    text: "Não foi possível criar o avatar virtual. Tente novamente.",
                });
            }
        },
        [roomId, user, sessionSetting]
    );

    const handleLeaveMeeting = useCallback(async () => {
        if (isLeavingRoom) return;
        setIsLeavingRoom(true);

        try {
            if (user && roomId) {
                // Se o usuário é terapeuta e está saindo da própria sala, desativa a sala
                if (sessionSetting?.isRoomModerator) {
                    await handleModeratorLeave();
                } else {
                    await removeUserFromRoom(roomId, user.uid);
                    await cancelRoomAccessRequestFunction({ roomId });
                }
            }
        } catch (error) {
            console.error("❌ Erro ao sair da reunião:", error);
        } finally {
            disableStreams();
            meetingClient?.leaveRoom();
            setTimeout(() => {
                // navegar de volta à página /painel com recaregamento da página
                window.location.href = "/painel";
            }, 500);
        }
    }, [disableStreams, handleModeratorLeave, isLeavingRoom, meetingClient, roomId, sessionSetting, user]);

    useEffect(() => {
        if (!meetingClient) return;

        if (!sessionSetting?.isRoomModerator) {
            setAccessRequests([]);
            previousPendingRequestsCount.current = 0;
            hasInitializedRequests.current = false;
            return;
        }

        const requestsRef = collection(db, "dyteMeetings", roomId, "accessRequests");
        const pendingQuery = query(requestsRef, where("status", "==", "pending"), orderBy("requestedAt", "asc"));

        const unsubscribe = onSnapshot(pendingQuery, (snapshot) => {
            const pendingRequests: RoomAccessRequest[] = snapshot.docs.map((docSnapshot) => {
                const data = docSnapshot.data() as Record<string, any>;
                return {
                    id: docSnapshot.id,
                    userId: data.userId as string,
                    status: data.status ?? "pending",
                    name: data.name ?? null,
                    email: data.email ?? null,
                    requestedAt: data.requestedAt?.toDate ? data.requestedAt.toDate() : null,
                } as RoomAccessRequest;
            });

            setAccessRequests(pendingRequests);
        }, (error) => {
            console.error("Erro ao ouvir solicitações de acesso:", error);
        });

        return () => unsubscribe();
    }, [roomId, sessionSetting?.isRoomModerator, meetingClient]);

    useEffect(() => {
        if (!sessionSetting?.isRoomModerator) {
            return;
        }

        if (!hasInitializedRequests.current) {
            hasInitializedRequests.current = true;
        } else if (accessRequests.length > previousPendingRequestsCount.current) {
            const newRequest = accessRequests[accessRequests.length - 1];
            const userName = newRequest?.name || newRequest?.email || "Um participante";
            
            const toast = Swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 4000,
                timerProgressBar: true,
                didOpen: (toast) => {
                    toast.addEventListener("mouseenter", Swal.stopTimer);
                    toast.addEventListener("mouseleave", Swal.resumeTimer);
                }
            });

            toast.fire({
                icon: "info",
                title: "Novo pedido de acesso",
                text: `${userName} solicitou entrada na sala.`
            });
        }

        previousPendingRequestsCount.current = accessRequests.length;
    }, [accessRequests, sessionSetting?.isRoomModerator]);

    // Monitorar saldo de créditos e alertar quando estiver baixo
    useEffect(() => {
        if (!sessionSetting?.isRoomModerator || !isLowBalance || hasShownLowCreditAlert.current) {
            return;
        }

        hasShownLowCreditAlert.current = true;

        Swal.fire({
            icon: "warning",
            title: "Créditos em horas baixos",
            html: `
                <p class="mb-2">Suas horas de terapia estão acabando!</p>
                <p class="mb-2">Saldo atual: <strong>${Math.round((hoursBalance || 0) * 60)} minutos</strong></p>
                <p class="text-sm text-gray-600">Faça uma recarga para continuar atendendo seus pacientes sem interrupções.</p>
            `,
            confirmButtonText: "Recarregar agora",
            showCancelButton: true,
            cancelButtonText: "Depois",
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#6b7280",
        }).then((result) => {
            if (result.isConfirmed) {
                window.open("/planos?secao=terapeuta", "_blank");
            }
        });
    }, [isLowBalance, hoursBalance, sessionSetting?.isRoomModerator]);

    const handleRespondToAccessRequest = useCallback(async (requestId: string, action: AccessRoomResponseOptions.APPROVE | AccessRoomResponseOptions.DENY) => {
        if (!sessionSetting?.isRoomModerator) {
            return;
        }

        setProcessingRequestId(requestId);

        try {
            const respond = httpsCallable<
                { roomId: string; requestId: string; action: AccessRoomResponseOptions.APPROVE | AccessRoomResponseOptions.DENY },
                { success: boolean; message?: string }
            >(functions, "respondToRoomAccessRequest");

            await respond({
                roomId,
                requestId,
                action,
            });
        } catch (error) {
            console.error("Erro ao responder solicitação de acesso:", error);
            Swal.fire({
                icon: "error",
                title: "Não foi possível processar o pedido",
                text: "Tente novamente em instantes.",
            });
        } finally {
            setProcessingRequestId(null);
        }
    }, [functions, roomId, sessionSetting?.isRoomModerator]);

    const dyteMeetingComponent = useMemo(() => {
        return (
            <>
                {meetingClient ? (
                    <DyteMeet
                        key={`dyte-${roomId}-${user?.uid}`}
                        meeting={meetingClient}
                        onMeetingEnd={handleLeaveMeeting}
                    />
                ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center text-white space-y-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                        <span>{loadingMessage || "Carregando videochamada..."}</span>
                    </div>
                )}
            </>
        )
    }, [meetingClient, loadingMessage, handleLeaveMeeting, roomId, user?.uid]);

    return (
        <div className="h-screen bg-slate-900 flex flex-col overflow-hidden">
            <ObservatorHandler roomId={roomId} user={user} />
            <InviteUserModal
                showInviteModal={showInviteModal}
                setShowInviteModal={setShowInviteModal}
                roomId={roomId}
                user={user}
            />

            <ParticipantsPanelModal
                meetingClient={meetingClient}
                roomId={roomId}
                user={user}
                sessionSetting={sessionSetting}
                isPanelOpen={isPanelOpen}
                setIsPanelOpen={setIsPanelOpen}
                accessRequests={accessRequests}
                onRespondToAccessRequest={handleRespondToAccessRequest}
                processingRequestId={processingRequestId}
            />

            <VirtualAvatarModal
                isOpen={isVirtualAvatarModalOpen}
                onClose={() => setIsVirtualAvatarModalOpen(false)}
                onConfirm={handleCreateVirtualAvatar}
            />

            <div className="flex flex-1 min-h-0">
                {/* Mobile */}
                <div
                    className={`flex-1 flex flex-col h-full ${isMobile ? "flex" : "hidden"}`}
                >
                    <header
                        className={`flex-shrink-0 bg-gray-800/95 backdrop-blur-md border-b border-gray-700/50 z-10 ${isPortrait ? "hidden" : "flex"}`}
                    >
                        <button
                            onClick={() => setActiveTab("scene")}
                            className={`flex-1 py-2 px-2 text-xs font-medium flex items-center justify-center space-x-2 transition-colors ${activeTab === "scene"
                                ? "bg-blue-600 text-white"
                                : "text-gray-300 hover:bg-gray-700"
                                }`}
                        >
                            <UserIcon className="w-4 h-4" /> <span>Cenário</span>
                        </button>
                        <button
                            onClick={() => setActiveTab("video")}
                            className={`flex-1 py-2 px-2 text-xs font-medium flex items-center justify-center space-x-2 transition-colors ${activeTab === "video"
                                ? "bg-blue-600 text-white"
                                : "text-gray-300 hover:bg-gray-700"
                                }`}
                        >
                            <Video className="w-4 h-4" /> <span>Videochamada</span>
                        </button>
                    </header>

                    <main className="flex-1 min-h-0 relative">
                        <div
                            className={`${isPortrait ? "flex" : "hidden"
                                } absolute top-0 left-0 right-0 bg-blue-600/90 text-white p-2.5 text-xs text-center items-center justify-center space-x-2 z-30`}
                        >
                            <span>
                                Para acessar o cenário, utilize o seu dispositivo na horizontal.
                            </span>
                        </div>

                        <div
                            className={`absolute inset-0 h-full w-full ${activeTab === "scene" && !isPortrait ? "block" : "hidden"}`}
                        >
                            <Scene
                                roomId={roomId}
                                onError={handleSceneError}
                                sessionSetting={sessionSetting}
                                meetingClient={meetingClient}
                            />
                        </div>
                        <div className={`absolute inset-0 h-full w-full ${activeTab === "video" ? "block" : "hidden"} ${isPortrait ? "pt-12" : ""}`}>
                            {meetingClient ? (
                                <DyteMeet
                                    key={`dyte-${roomId}-${user?.uid}`}
                                    meeting={meetingClient}
                                    onMeetingEnd={handleLeaveMeeting}
                                />
                            ) : (
                                <div className="h-full w-full flex flex-col items-center justify-center text-white space-y-3">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                                    <span>{loadingMessage || "Carregando videochamada..."}</span>
                                </div>
                            )}
                        </div>
                    </main>

                    <footer className="flex-shrink-0 z-20">
                        <ActionButtons
                            isModerator={sessionSetting?.isRoomModerator || false}
                            isMobile={true}
                            roomId={roomId}
                            user={user}
                            isLeavingRoom={isLeavingRoom}
                            isMeetingLoaded={!!meetingClient}
                            setShowInviteModal={setShowInviteModal}
                            setIsPanelOpen={setIsPanelOpen}
                            handleLeaveMeeting={handleLeaveMeeting}
                            onAddVirtualAvatar={() => setIsVirtualAvatarModalOpen(true)}
                            pendingApprovalsCount={sessionSetting?.isRoomModerator ? accessRequests.length : 0}
                        />
                    </footer>
                </div>

                {/* Desktop */}
                <div
                    className={`flex-1 min-h-0 flex relative ${isMobile ? "hidden" : "flex"}`}
                >
                    <main className="flex-1 flex min-h-0 pb-20">
                        <div className="flex-1 relative min-w-0">
                            <div className="absolute inset-0">
                                <Scene
                                    roomId={roomId}
                                    onError={handleSceneError}
                                    sessionSetting={sessionSetting}
                                    meetingClient={meetingClient}
                                />
                            </div>
                        </div>

                        <aside className="w-80 bg-gray-800/95 backdrop-blur-md shadow-2xl border-l border-gray-700/50 flex flex-col">
                            <header className="p-3 border-b border-gray-700/50">
                                <h3 className="text-base font-semibold text-gray-100 flex items-center space-x-2">
                                    <Video className="w-4 h-4 text-blue-500" />
                                    <span>Videochamada</span>
                                </h3>
                            </header>
                            <div className="flex-1 min-h-0">
                                <div className="h-full w-full bg-gray-900 overflow-hidden">
                                    {dyteMeetingComponent}
                                </div>
                            </div>
                        </aside>
                    </main>
                    <footer className="absolute bottom-0 left-0 right-0 z-[60]">
                        <div className="px-4 py-3 flex justify-center">
                            <ActionButtons
                                isModerator={sessionSetting?.isRoomModerator || false}
                                isMobile={false}
                                roomId={roomId}
                                user={user}
                                isLeavingRoom={isLeavingRoom}
                                isMeetingLoaded={!!meetingClient}
                                setShowInviteModal={setShowInviteModal}
                                setIsPanelOpen={setIsPanelOpen}
                                handleLeaveMeeting={handleLeaveMeeting}
                                onAddVirtualAvatar={() => setIsVirtualAvatarModalOpen(true)}
                                pendingApprovalsCount={sessionSetting?.isRoomModerator ? accessRequests.length : 0}
                            />
                        </div>
                    </footer>
                </div>
            </div>
        </div>
    );
}

export default Room;